import React, { useCallback, useEffect, useState, createElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenant } from '../../lib/tenant-context';
import { supabase } from '../../lib/supabase';
import { Table } from '../../lib/types';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { QrCode, Printer, Download, ArrowLeft, Armchair, Store } from 'lucide-react';
import QRCode from 'qrcode';
interface TableWithQR {
  table: Table;
  qrDataUrl: string;
  url: string;
}
function getTableQRUrl(slug: string, tableNumber: string, qrToken: string | null): string {
  const token = qrToken || tableNumber;
  const hostname = window.location.hostname.toLowerCase();
  const protocol = window.location.protocol;
  const port = window.location.port ? ':' + window.location.port : '';
  // Always use path-based URL (apex domain) — wildcard SSL is not guaranteed
  // for restaurant subdomains, so subdomain QR URLs would fail in Safari/iOS.
  // Path-based works on the same domain that already has a valid cert.
  const baseDomains = ['noizacr.com', 'noiza.estrategiasdigitalesaz.com'];
  for (const base of baseDomains) {
    if (hostname === base || hostname.endsWith('.' + base)) {
      return protocol + '//' + base + '/r/' + slug + '/mesa/' + tableNumber + '/' + token;
    }
  }
  // Dev / fallback: use current hostname
  return protocol + '//' + hostname + port + '/r/' + slug + '/mesa/' + tableNumber + '/' + token;
}
export function TableQRPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = useTenant();
  const [tablesWithQR, setTablesWithQR] = useState<TableWithQR[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!restaurant) return;
    async function fetchTablesAndGenerateQRs() {
      try {
        setLoading(true);
        const {
          data: tablesData,
          error
        } = await supabase.from('tables').select('*').eq('restaurant_id', restaurant!.id).eq('is_active', true).order('table_number');
        if (error) throw error;
        if (tablesData) {
          const processedTables = await Promise.all(tablesData.map(async (table: Table) => {
            const url = getTableQRUrl(restaurant!.slug, table.table_number, table.qr_token);
            const qrDataUrl = await QRCode.toDataURL(url, {
              width: 400,
              margin: 2,
              color: {
                dark: '#000000',
                light: '#ffffff'
              }
            });
            return {
              table,
              qrDataUrl,
              url
            };
          }));
          setTablesWithQR(processedTables);
        }
      } catch (err) {
        console.error('Error fetching tables or generating QRs:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchTablesAndGenerateQRs();
  }, [restaurant]);
  const handleDownload = (qrDataUrl: string, tableName: string) => {
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `QR-${tableName.replace(/\s+/g, '-')}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrintAll = () => {
    window.print();
  };
  const handlePrintSingle = (tableQR: TableWithQR) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const printCss = ['body { font-family: system-ui, -apple-system, sans-serif; display: flex; justify-content: center; align-items: center; min-height: 100vh; margin: 0; background: #f3f4f6; }', '.qr-card { background: white; padding: 2rem; border-radius: 1rem; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); text-align: center; max-width: 400px; width: 100%; }', '.logo { width: 80px; height: 80px; border-radius: 1rem; object-fit: cover; margin-bottom: 1rem; }', '.restaurant-name { font-size: 1.25rem; font-weight: bold; color: #111827; margin: 0 0 0.5rem 0; }', '.table-name { font-size: 1.5rem; font-weight: 900; color: #000; margin: 0 0 1.5rem 0; }', '.qr-image { width: 100%; max-width: 300px; height: auto; margin: 0 auto 1.5rem auto; display: block; }', '.instruction { font-size: 1.125rem; font-weight: 600; color: #4b5563; margin: 0; }', '@media print { body { background: white; align-items: flex-start; padding-top: 2rem; } .qr-card { box-shadow: none; border: 2px dashed #e5e7eb; } }'].join('\n');
    // Build style/script tags as concatenated strings to avoid the TSX
    // formatter treating them as JSX inside the template literal below.
    const styleTag = '<' + 'style>' + printCss + '<' + '/style>';
    const scriptBody = 'window.onload = () => { window.print(); setTimeout(() => window.close(), 500); };';
    const scriptTag = '<' + 'script>' + scriptBody + '<' + '/script>';
    const logoHtml = restaurant?.logo_url ? '<img src="' + restaurant.logo_url + '" alt="Logo" class="logo" />' : '';
    const tableLabel = tableQR.table.table_name || 'Mesa ' + tableQR.table.table_number;
    const html = '<!DOCTYPE html>' + '<html><head>' + '<title>Imprimir QR - ' + tableLabel + '</title>' + styleTag + '</head><body>' + '<div class="qr-card">' + logoHtml + '<h2 class="restaurant-name">' + (restaurant?.name || '') + '</h2>' + '<h1 class="table-name">' + tableLabel + '</h1>' + '<img src="' + tableQR.qrDataUrl + '" alt="QR Code" class="qr-image" />' + '<p class="instruction">Escaneá para autoatenderte</p>' + '</div>' + scriptTag + '</body></html>';
    printWindow.document.write(html);
    printWindow.document.close();
  };
  if (restLoading || !restaurant) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-4850">
        <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-6" data-id="element-4851">
          <div className="w-16 h-16 rounded-full bg-[#141414] animate-pulse" data-id="element-4852" />
        </div>
      </AdminLayout>;
  }
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-4853">
      {/* Print Styles */}
      <style dangerouslySetInnerHTML={{
      __html: `
        @media print {
          body * {
            visibility: hidden;
          }
          .print-container, .print-container * {
            visibility: visible;
          }
          .print-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            display: grid !important;
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 2rem !important;
            padding: 2rem !important;
            background: white !important;
          }
          .no-print {
            display: none !important;
          }
          .qr-print-card {
            break-inside: avoid;
            page-break-inside: avoid;
            border: 2px dashed #e5e7eb !important;
            background: white !important;
            color: black !important;
          }
        }
      `
    }} data-id="element-4854" />

      <div className="min-h-screen bg-[#0B0B0B] p-4 sm:p-6 text-white font-sans" data-id="element-4855">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 no-print" data-id="element-4856">
          <div className="flex items-center gap-4" data-id="element-4857">
            <Link to={`/admin/${restaurant.slug}/mesas`} className="p-2 -ml-2 text-white/50 hover:text-white rounded-full hover:bg-white/10 transition-colors" data-id="element-4858">
              <ArrowLeft className="w-6 h-6" data-id="element-4859" />
            </Link>
            <div data-id="element-4860">
              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-3" data-id="element-4861">
                Códigos QR
              </h1>
              <p className="text-white/50 text-sm mt-1" data-id="element-4862">
                Gestiona e imprime los códigos QR para autoatención
              </p>
            </div>
          </div>
          <div className="flex gap-3" data-id="element-4863">
            <Button variant="secondary" onClick={handlePrintAll} disabled={loading || tablesWithQR.length === 0} className="bg-white/10 hover:bg-white/20 text-white border-0" data-id="element-4864">
              <Printer className="w-4 h-4 mr-2" data-id="element-4865" />
              Imprimir Todos
            </Button>
          </div>
        </div>

        {/* Content */}
        {loading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 no-print" data-id="element-4866">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-[400px] rounded-2xl bg-[#141414]" data-id="element-4867" />)}
          </div> : tablesWithQR.length === 0 ? <div className="no-print" data-id="element-4868">
            <EmptyState icon={Armchair} title="No hay mesas activas" description="Debes crear y activar mesas en la configuración para generar sus códigos QR." actionLabel="Volver a Mesas" onAction={() => window.history.back()} data-id="element-4869" />
          </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 print-container" data-id="element-4870">
            {tablesWithQR.map(({
          table,
          qrDataUrl
        }) => {
          const displayName = table.table_name || `Mesa ${table.table_number}`;
          return <div key={table.id} className="bg-white rounded-2xl p-6 flex flex-col items-center text-center qr-print-card shadow-xl" data-id="element-4871">
                  {/* Printable Area */}
                  <div className="flex-1 flex flex-col items-center justify-center w-full mb-6" data-id="element-4872">
                    {restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-16 h-16 rounded-xl object-cover mb-3 shadow-sm border border-gray-100" data-id="element-4873" /> : <div className="w-16 h-16 rounded-xl bg-gray-100 flex items-center justify-center mb-3" data-id="element-4874">
                        <Store className="w-8 h-8 text-gray-400" data-id="element-4875" />
                      </div>}
                    <h3 className="text-gray-500 font-bold text-sm uppercase tracking-wider mb-1" data-id="element-4876">
                      {restaurant.name}
                    </h3>
                    <h2 className="text-3xl font-black text-gray-900 mb-6" data-id="element-4877">
                      {displayName}
                    </h2>

                    <div className="bg-white p-2 rounded-xl shadow-sm border border-gray-100 mb-6" data-id="element-4878">
                      <img src={qrDataUrl} alt={`QR ${displayName}`} className="w-full max-w-[200px] h-auto" data-id="element-4879" />
                    </div>

                    <p className="text-lg font-bold text-gray-800" data-id="element-4880">
                      Escaneá para autoatenderte
                    </p>
                    <p className="text-xs font-medium text-gray-400 mt-2" data-id="element-4881">
                      Autoatención rápida y segura
                    </p>
                  </div>

                  {/* Actions (Hidden in print) */}
                  <div className="w-full grid grid-cols-2 gap-3 pt-4 border-t border-gray-100 no-print" data-id="element-4882">
                    <Button variant="secondary" onClick={() => handleDownload(qrDataUrl, displayName)} className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 border-0" data-id="element-4883">
                      <Download className="w-4 h-4 mr-2" data-id="element-4884" />
                      PNG
                    </Button>
                    <Button variant="secondary" onClick={() => handlePrintSingle({
                table,
                qrDataUrl,
                url: ''
              })} className="w-full bg-gray-50 hover:bg-gray-100 text-gray-700 border-0" data-id="element-4885">
                      <Printer className="w-4 h-4 mr-2" data-id="element-4886" />
                      Imprimir
                    </Button>
                  </div>
                </div>;
        })}
          </div>}
      </div>
    </AdminLayout>;
}